import React from 'react';
import { Box, Flex, Radio as DsRadio, Text } from '@commonsku/design-system';
import { Col, Label } from '@commonsku/styles';

export const BUILD_MODE_SKUBOT = 'skubot';
export const BUILD_MODE_MANUAL = 'manual';

export type BuildMode = typeof BUILD_MODE_SKUBOT | typeof BUILD_MODE_MANUAL;

type BuildModeOption = {
  value: BuildMode;
  title: string;
  description: string;
};

const OPTIONS: BuildModeOption[] = [
  {
    value: BUILD_MODE_SKUBOT,
    title: 'Build with skubot',
    description: 'skubot picks products and builds the presentation for you. You refine from there.',
  },
  {
    value: BUILD_MODE_MANUAL,
    title: 'Build manually',
    description: 'You manually pick products and build the presentation',
  },
];

function BuildModeRadioIndicator({ selected }: { selected: boolean }) {
  return (
    <DsRadio.Root variant="solid" size="md" value={selected ? 'on' : null} pointerEvents="none">
      <DsRadio.Item value="on">
        <DsRadio.ItemHiddenInput readOnly tabIndex={-1} />
        <DsRadio.ItemIndicator
          colorPalette="skuBlue"
          css={{
            '&[data-checked] .dot': {
              width: '20px',
              height: '20px',
              background: 'white',
              borderRadius: '9999px',
            },
          }}
        />
      </DsRadio.Item>
    </DsRadio.Root>
  );
}

type BuildModeSelectProps = {
  value: BuildMode;
  onChange: (value: BuildMode) => void;
};

export default function BuildModeSelect({ value, onChange }: BuildModeSelectProps) {
  return (
    <Col xs padded>
      <Label>Build mode</Label>
      <Flex role="radiogroup" aria-label="Build mode" gap="12px" w="full">
        {OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <Box
              key={option.value}
              as="button"
              role="radio"
              aria-checked={selected}
              flex="1"
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="8px"
              m="0"
              p="16px"
              textAlign="left"
              cursor="pointer"
              bg="white"
              borderRadius="8px"
              border="2px solid"
              borderColor={selected ? 'skuBlue.600' : 'gray.200'}
              boxShadow="none"
              appearance="none"
              _hover={{
                borderColor: selected ? 'skuBlue.600' : 'gray.300',
              }}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'skuBlue.600',
                outlineOffset: '2px',
              }}
              onClick={() => onChange(option.value)}
            >
              <Flex align="flex-start" justify="space-between" gap="8px" w="full">
                <Text
                  margin="0"
                  fontSize="sm"
                  fontWeight="medium"
                  lineHeight="20px"
                  color="gray.900"
                >
                  {option.title}
                </Text>
                <BuildModeRadioIndicator selected={selected} />
              </Flex>
              <Text
                margin="0"
                fontSize="xs"
                fontWeight="normal"
                lineHeight="16px"
                color="gray.600"
              >
                {option.description}
              </Text>
            </Box>
          );
        })}
      </Flex>
    </Col>
  );
}
